import { onMounted, ref, toValue } from 'vue'
import { useStore } from 'vuex'

import NotificationsApiHandler from '@/api/notifications'
import WebAppUsersApiHandler from '@/api/web-app-users'
import { handleError } from '@/utils/error-handling'
import { NOTIFICATIONS_SETTINGS_CONFIG } from '@/utils/in-app-notifications'

export const useRenameUser = ({ initialName = '' } = {}) => {
  const prevName = ref(initialName)
  const isNameValid = ref(true)
  const name = ref(initialName)
  const loading = ref(false)

  const store = useStore()

  const onRenameUser = async () => {
    const resolvedName = name.value.trim()
    isNameValid.value = resolvedName.length > 0

    if (isNameValid.value && resolvedName !== prevName.value) {
      loading.value = true
      const api = new WebAppUsersApiHandler()

      try {
        const { name: newName } = await api.renameUser({ name: resolvedName })
        await store.dispatch('system/updateUserName', newName)
        prevName.value = newName
        // eslint-disable-next-line no-useless-catch
      } catch (error) {
        isNameValid.value = false
        throw error
      } finally {
        loading.value = false
      }
    }
  }

  return {
    name,
    isNameValid,
    onRenameUser,
    loading
  }
}

export const useSetSubscriptionsForUser = ({ localSubscription = null } = {}) => {
  const notificationTypeAndChannels = ref([])

  const fetchTypeAndChannels = async () => {
    const api = new NotificationsApiHandler()

    try {
      notificationTypeAndChannels.value = await api.getNotificationTypeAndChannels()
    } catch (error) {
      handleError({ error })
    }
  }

  const setSubscriptionsForUser = async () => {
    const { MARKETING_PRODUCT_UPDATE, MARKETING_PRODUCT_CASE_STUDY, MARKETING_PRODUCT_PROMOS } =
      NOTIFICATIONS_SETTINGS_CONFIG.config

    const subscriptionsTypeIds = [
      MARKETING_PRODUCT_UPDATE.typeId,
      MARKETING_PRODUCT_CASE_STUDY.typeId,
      MARKETING_PRODUCT_PROMOS.typeId
    ]

    const payload = subscriptionsTypeIds.map(notificationTypeId => {
      const currentNotificationSettings = toValue(notificationTypeAndChannels).find(
        item => Number(item.id) === notificationTypeId
      )

      let notificationChannelsIds = toValue(localSubscription)
        ? (currentNotificationSettings?.channels || []).map(({ id }) => id)
        : []

      return {
        notificationTypeId,
        notificationChannelsIds
      }
    })

    try {
      const api = new NotificationsApiHandler()

      await api.patchNotificationSettings({
        notificationsSettings: payload
      })
    } catch (error) {
      handleError({ error })
    }
  }

  onMounted(() => {
    fetchTypeAndChannels()
  })

  return {
    notificationTypeAndChannels,
    setSubscriptionsForUser,
    fetchTypeAndChannels
  }
}
